import { CircularProgress, TableBody, TableRow } from '@mui/material';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import {
  StoreRequest,
  StoreRequestBundle,
} from '../../../../shared/api/hscan/hospital';
import {
  useStoreBundleList,
  useStoreFromsInBundle,
} from '../../../../shared/hooks/hospitalHook';
import expandDown from '../../assets/icons/icon_expand-down.svg';
import expandRight from '../../assets/icons/icon_expand-right.svg';
import { IconButton } from '../../common/components/Icon';
import { HscanTableCell } from '../../common/components/SortableTable';
import {
  PresentationalTableProps,
  TableAndElementInformation,
  createTableElementInformationContext,
  createTableInformationContext,
} from '../../common/components/SortableTable/PresentationalTable';
import {
  BasicTable,
  BasicTableElement,
  FragmentTableBody,
  handleCellStyle,
} from '../../common/components/SortableTable/RenderTable';
import { useModalityLabel } from '../../hooks/modalityHook';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

import { MobileUploadedImageListTable } from './MobileUploadedImageListTable';
import { StatusMessage } from './StatusMessage';
import { UploadedImageHistoryModal } from './UploadedImageHistoryModal';

export const {
  TableContext: BundleTableContext,
  TableInformationProvider: BundleTableInformationProvider,
} = createTableInformationContext<StoreRequestBundle, 'id'>();
export const {
  TableElementContext: BundleTableElementContext,
  TableElementInformationProvider: BundleTableElementInformationProvider,
} = createTableElementInformationContext<StoreRequestBundle, 'id'>(
  BundleTableContext,
);

const DEFAULT_COUNT_PER_PAGE_OPTIONS = [10, 20, 30];

export const IconButtonWrapper = styled.div`
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const UploadedImageListTable = (
  props: Omit<
    PresentationalTableProps<StoreRequestBundle, 'id'>,
    'identifyKey' | 'data'
  >,
) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.uploadedImages.table',
  });

  const [page, setPage] = useState(0);
  const [countPerPage, setCountPerPage] = useState(
    DEFAULT_COUNT_PER_PAGE_OPTIONS[0],
  );

  const { data, isFetching } = useStoreBundleList({
    page,
    size: countPerPage,
  });
  const storeBundleList = data?.content ?? [];

  const [openModal, setOpenModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string>();

  const onClickStatus = useCallback((el: StoreRequest) => {
    setSelectedRowId(el.id);
    setOpenModal(true);
  }, []);

  const columnHelper = useMemo(
    () => createColumnHelper<StoreRequestBundle>(),
    [],
  );
  const bundleCellOptions = useMemo(
    () => [
      columnHelper.display({
        id: '__expand',
        cell: ({ row }) => {
          const isExpanded = row.getIsExpanded();
          return (
            <IconButtonWrapper onClick={row.getToggleExpandedHandler()}>
              <IconButton
                imageUrl={isExpanded ? expandDown : expandRight}
                width={isExpanded ? '14px' : '8px'}
                height={isExpanded ? '8px' : '14px'}
                color="#666973"
              />
            </IconButtonWrapper>
          );
        },
        meta: {
          cellStyle: { borderBottom: { style: 'none' } },
        },
      }),
      columnHelper.accessor('id', {
        header: t('header.id') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('createdAt', {
        header: t('header.requestDate') as string,
        cell: ({ getValue }) =>
          moment(getValue()).format('YYYY.MM.DD HH:mm:ss'),
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('hospitalName', {
        header: t('header.hospitalName') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('requestIds', {
        header: t('header.numImages') as string,
        cell: ({ getValue }) => getValue().length,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.display({
        id: '__EMPTY__',
      }),
    ],
    [columnHelper, t],
  );

  const windowSize = useContext(WindowSizeContext);

  const Element = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-shadow
      props: TableAndElementInformation<StoreRequestBundle, 'id'>,
    ) => <StoreRequestBundleElement onClickStatus={onClickStatus} {...props} />,
    [onClickStatus],
  );

  return (
    <>
      <BundleTableInformationProvider
        {...props}
        disableSelect
        data={storeBundleList}
        cellOptions={bundleCellOptions}
        totalCount={data?.totalElements}
        countPerPage={countPerPage}
        countPerPageOptions={DEFAULT_COUNT_PER_PAGE_OPTIONS}
        page={page}
        onChangePage={setPage}
        onChangeCountPerPage={newCountPerPage => {
          setPage(0);
          setCountPerPage(newCountPerPage);
        }}
        isLoading={isFetching}
        identifyKey="id">
        {windowSize === WindowSize.MOBILE ? (
          <MobileUploadedImageListTable onClickStatus={onClickStatus} />
        ) : (
          <BasicTable
            tableContext={BundleTableContext}
            elementContext={BundleTableElementContext}
            ElementProvider={BundleTableElementInformationProvider}
            Element={Element}
            TableBody={FragmentTableBody}
          />
        )}
      </BundleTableInformationProvider>
      <UploadedImageHistoryModal
        id={selectedRowId ?? ''}
        open={openModal}
        onClose={() => setOpenModal(false)}
      />
    </>
  );
};

const StoreRequestBundleTableBody = styled(TableBody)`
  border-bottom: 1px solid #e5e7eb;
`;

const StoreRequestBundleElement = ({
  onClickStatus,
  ...props
}: TableAndElementInformation<StoreRequestBundle, 'id'> & {
  onClickStatus: (storeRequest: StoreRequest) => void;
}) => {
  const { row, element: bundle, page } = props;

  useEffect(() => {
    if (row.getIsExpanded()) {
      row.toggleExpanded(false);
    }
  }, [row, page]);

  return (
    <StoreRequestBundleTableBody>
      <BasicTableElement {...props} />
      {row.getIsExpanded() && (
        <StoreRequestsTable bundle={bundle} onClickStatus={onClickStatus} />
      )}
    </StoreRequestBundleTableBody>
  );
};

// TODO: StudyIssuanceRequestsTableRow 와 함께 NestedTableRow 로 변경하기
const StoreRequestsTableRow = styled(TableRow)`
  background: #f8f9fd;
`;

export interface StoreRequestsTableProps {
  bundle: StoreRequestBundle;
  onClickStatus: (storeRequest: StoreRequest) => void;
}
const StoreRequestsTable = ({
  bundle,
  onClickStatus,
}: StoreRequestsTableProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.uploadedImages.table',
  });
  const { data: storeRequests, isFetching } = useStoreFromsInBundle({
    bundleId: bundle.id,
  });
  const modalityLabel = useModalityLabel();

  const columnHelper = useMemo(() => createColumnHelper<StoreRequest>(), []);
  const cellOptions = useMemo(
    () => [
      columnHelper.display({
        id: '__PLACEHOLDER__',
        meta: {
          cellStyle: { background: 'white', borderBottom: { style: 'none' } },
          headerStyle: { background: 'white', borderBottom: { style: 'none' } },
        },
      }),
      columnHelper.accessor('studyDate', {
        header: t('header.imageDate') as string,
        cell: ({ getValue }) => moment(getValue()).format('YYYY.MM.DD'),
      }),
      columnHelper.accessor('institutionName', {
        header: t('header.hospitalNameInStore') as string,
        meta: {
          cellStyle: { align: 'justify' },
        },
      }),
      columnHelper.accessor('modalities', {
        id: 'modalities',
        header: t('header.modalities') as string,
        cell: ({ getValue }) =>
          getValue()
            ?.map(modality => modalityLabel(modality))
            .join(', '),
      }),
      columnHelper.accessor('studyDescription', {
        header: t('header.studyDescription') as string,
      }),
      columnHelper.accessor('status', {
        header: t('header.status') as string,
        cell: ({ getValue, row: { original: storeRequest } }) => (
          <div
            style={{ cursor: 'pointer' }}
            onClick={() => onClickStatus(storeRequest)}>
            <StatusMessage status={getValue()} />
          </div>
        ),
      }),
    ],
    [columnHelper, modalityLabel, onClickStatus, t],
  );

  const table = useReactTable({
    data: storeRequests ?? [],
    columns: cellOptions,
    getCoreRowModel: getCoreRowModel(),
  });

  return isFetching ? (
    <TableRow>
      <HscanTableCell colSpan={cellOptions.length + 1}>
        <CircularProgress />
      </HscanTableCell>
    </TableRow>
  ) : storeRequests ? (
    <>
      <StoreRequestsTableRow>
        {table.getLeafHeaders().map(header => (
          <HscanTableCell
            key={header.id}
            {...handleCellStyle(header.column.columnDef.meta?.headerStyle)}
            align="center">
            {
              flexRender(
                header.column.columnDef.header,
                header.getContext(),
              ) as React.ReactNode
            }
          </HscanTableCell>
        ))}
      </StoreRequestsTableRow>
      {table.getRowModel().rows.map(row => (
        <TableRow key={row.id}>
          {row.getVisibleCells().map(cell => (
            <HscanTableCell
              key={cell.id}
              {...handleCellStyle(cell.column.columnDef.meta?.cellStyle)}
              align="center">
              {
                flexRender(
                  cell.column.columnDef.cell,
                  cell.getContext(),
                ) as React.ReactNode
              }
            </HscanTableCell>
          ))}
        </TableRow>
      ))}
    </>
  ) : (
    <>error</>
  );
};
